/**
 * Created by fers on 26.09.15.
 */

import { v4 as uuidv4 } from 'uuid';
import { declareLinkedEntity } from '../fw/MetaDataHelper.ts';
import * as Permission from '../util/Permission';

export const MAPPING_TYPE_COPY = "copy";
export const MAPPING_TYPE_IGNORE = "ignore";
export const MAPPING_TYPE_FIXED = "fixed";
export const MAPPING_TYPE_EMBEDDED = "embedded";
export const MAPPING_TYPE_LOOKUP = "lookup";
export const MAPPING_TYPE_DEFAULT_EXPRESSION = "default-expression";
export const MAPPING_TYPE_EXPRESSION = "expression";

export const DEFAULT_FIELD_ORDER = "10";

export default declareLinkedEntity({
    entity: "channelFieldDefs",
    graphQL: "ChannelFieldDef",
    name: "Channel Field Definition",
    defaultOrder: ['jsonQuery', 'type'],
    writePermission: [
        Permission.PERM_EDIT_CHANNEL_DEF
    ],
    dynamoPrefix: 'def',
    storageType: 'schema',
    parentRelation: 'channelFieldDefs',
    onModify: ['channelDef'],
    fields: (types) => { return {
        id: {
            name: "Channel Field ID",
            type: "id",
            defaultValue: () => {
                return uuidv4();
            },
            primaryKey: true,
            required: true,
            userEditable: false,
        },
        channelEntityDef: {
            name: "Channel Entity",
            to: () => types['channelEntityDefs'],
            type: "relation",
            defaultValue: (channelEntityDef) => {
                return channelEntityDef['id'];
            },
            required: true,
            parent: true,
            compositeKey: true,
            hiddenCreate: true,
            relativeTo: ['channelDef'],
        },
        name: {
            name: "Name",
            type: "string",
            defaultValue: "",
            translatable: true,
            isPublic: true,
            visibility: (data) => { return data['type'] === MAPPING_TYPE_COPY; }
        },
        description: {
            name: "Description",
            type: "string",
            defaultValue: "",
            translatable: true,
            isPublic: true,
            visibility: (data) => { return data['type'] === MAPPING_TYPE_COPY; }
        },
        order: {
            name: "Order",
            type: "string",
            defaultValue: DEFAULT_FIELD_ORDER,
            visibility: (data) => { return data['type'] === MAPPING_TYPE_COPY; }
        },
        jsonQuery: {
            name: "JSON Query",
            type: "string",
            trim: true,
            defaultValue: "",
            visibility: (data) => { return data['type'] === MAPPING_TYPE_COPY || data['type'] === MAPPING_TYPE_IGNORE || data['type'] === MAPPING_TYPE_EMBEDDED || data['type'] === MAPPING_TYPE_FIXED; },
        },
        filteringEnabled: {
            name: "Filtering enabled",
            type: "boolean",
            defaultValue: false,
            visibility: (data) => { return data['type'] === MAPPING_TYPE_COPY; }
        },
        modifyAlias: {
            name: "Modify alias",
            description: "Leave empty to use default alias.",
            type: "string",
            defaultValue: "",
            visibility: (data) => { return data['type'] === MAPPING_TYPE_COPY && (data['actionCreateEnabled'] || data['actionUpdateEnabled']); },
        },
        actionUpdateEnabled: {
            name: "Action Update enabled",
            type: "boolean",
            defaultValue: false,
            visibility: (data) => { return data['type'] === MAPPING_TYPE_COPY; }
        },
        actionCreateEnabled: {
            name: "Action Create enabled",
            type: "boolean",
            defaultValue: false,
            visibility: (data) => { return data['type'] === MAPPING_TYPE_COPY; }
        },
        orderingEnabled: {
            name: "Ordering enabled",
            type: "boolean",
            defaultValue: false,
            visibility: (data) => { return data['type'] === MAPPING_TYPE_COPY; }
        },
        filteringAlias: {
            name: "Filtering alias",
            type: "string",
            defaultValue: "",
            visibility: (data) => { return data['type'] === MAPPING_TYPE_COPY && (data['filteringEnabled'] || data['orderingEnabled']); }
        },
        type: {
            name: "Type",
            type: "enum",
            defaultValue: MAPPING_TYPE_COPY,
            values: {
                [MAPPING_TYPE_COPY]: {
                    name: "Copy"
                },
                [MAPPING_TYPE_IGNORE]: {
                    name: "Ignore"
                },
                [MAPPING_TYPE_FIXED]: {
                    name: "Fixed value",
                },
                [MAPPING_TYPE_EXPRESSION]: {
                    name: "Expression",
                },
                [MAPPING_TYPE_DEFAULT_EXPRESSION]: {
                    name: "Default expression",
                },
                [MAPPING_TYPE_LOOKUP]: {
                    name: "Lookup"
                },
                [MAPPING_TYPE_EMBEDDED]: {
                    name: "Embedded data"
                }
            }
        },
        format: {
            name: "Format",
            type: "string",
            trim: true,
            defaultValue: "",
            visibility: (data) => { return data['type'] === MAPPING_TYPE_COPY; }
        },
        fixedValue: {
            name: "Fixed value",
            type: "string",
            trim: true,
            defaultValue: "",
            visibility: (data) => { return data['type'] === MAPPING_TYPE_FIXED; }
        },
        entityDef: {
            name: "Entity Definition",
            to: () => types['entityDefs'],
            type: "relation",
            visibility: (data) => { return data['type'] === MAPPING_TYPE_COPY || data['type'] === MAPPING_TYPE_FIXED || data['type'] === MAPPING_TYPE_DEFAULT_EXPRESSION || data['type'] === MAPPING_TYPE_EXPRESSION || data['type'] === MAPPING_TYPE_LOOKUP; },
        },
        fieldDef: {
            name: "Field Definition",
            to: () => types['fieldDefs'],
            type: "relation",
            visibility: (data) => { return data['type'] === MAPPING_TYPE_COPY || data['type'] === MAPPING_TYPE_FIXED || data['type'] === MAPPING_TYPE_DEFAULT_EXPRESSION || data['type'] === MAPPING_TYPE_EXPRESSION || data['type'] === MAPPING_TYPE_LOOKUP; },
            relativeTo: ['entityDef'],
        },
        channelDef: {
            name: "Application Definition",
            to: () => types['channelDefs'],
            type: "relation",
            defaultValue: (channelEntityDef) => {
                return channelEntityDef['channelDef_id'];
            },
            hiddenCreate: true,
            compositeKey: true,
            visibility: (data) => { return data['type'] === MAPPING_TYPE_EMBEDDED || data['type'] === MAPPING_TYPE_LOOKUP; }
        },
        channelDefVersion: {
            name: "Channel Definition Version",
            to: () => types['channelDefVersions'],
            type: "relation",
            defaultValue: (channelDef) => {
                return channelDef['channelDefVersion_id'];
            },
            required: true,
            hiddenCreate: true,
            compositeKey: true,
        },
        embeddedChannelEntityDef: {
            name: "Embedded Channel Entity",
            to: () => types['channelEntityDefs'],
            type: "relation",
            relativeTo: ['channelDef'],
            visibility: (data) => { return data['type'] === MAPPING_TYPE_EMBEDDED; }
        },
        entityRelationDef: {
            name: "Entity Relation Definition",
            to: () => types['entityRelationDefs'],
            type: "relation",
            relativeTo: ['entityRelationEntityDef'],
            visibility: (data) => { return data['type'] === MAPPING_TYPE_EMBEDDED; }
        },
        entityRelationEntityDef: {
            name: "Entity Relation Definition Entity",
            to: () => types['entityDefs'],
            type: "relation",
            visibility: (data) => { return data['type'] === MAPPING_TYPE_EMBEDDED; }
        },
        channelRelationDef: {
            name: "Relation",
            to: () => types['channelRelationDefs'],
            type: "relation",
            relativeTo: ['channelDef'],
            visibility: (data) => { return data['type'] === MAPPING_TYPE_LOOKUP; }
        },
        lookupEntityDef: {
            name: "Lookup Entity Definition",
            to: () => types['entityDefs'],
            type: "relation",
            visibility: (data) => { return data['type'] === MAPPING_TYPE_LOOKUP; }
        },
        lookupFieldDef: {
            name: "Lookup Field",
            to: () => types['fieldDefs'],
            type: "relation",
            relativeTo: ['lookupEntityDef'],
            visibility: (data) => { return data['type'] === MAPPING_TYPE_LOOKUP; }
        },
        multipleValues: {
            name: "Has multiple values",
            type: "boolean",
            defaultValue: false,
            visibility: (data) => { return data['type'] === MAPPING_TYPE_COPY || data['type'] === MAPPING_TYPE_FIXED || data['type'] === MAPPING_TYPE_DEFAULT_EXPRESSION || data['type'] === MAPPING_TYPE_EXPRESSION || data['type'] === MAPPING_TYPE_LOOKUP; }
        },
        operations: {
            name: "Operation",
            type: "object",
            defaultValue: {},
            visibility: (data) => { return data['type'] === MAPPING_TYPE_COPY; }
        },
        defaultExpression: {
            name: "Expression",
            type: "expression",
            visibility: (data) => { return data['type'] === MAPPING_TYPE_EXPRESSION || data['type'] === MAPPING_TYPE_COPY; },
        },
        defaultValue: {
            name: "Default value",
            type: "string",
            visibility: (data) => { return data['type'] === MAPPING_TYPE_COPY; }
        },
        devNote: {
            name: "Developer Note",
            type: "string",
        },
        author: {
            name: "Author",
            type: "relation",
            to: () => types['users'],
            userEditable: false,
        },
        updatedAt: {
            name: "Updated At",
            type: "datetime",
            defaultValue: () => {
                return new Date();
            },
            userEditable: false,
        },
        createdAt: {
            name: "Created At",
            type: "datetime",
            defaultValue: () => {
                return new Date();
            },
            userEditable: false,
        }
    }; },
    relations: (types) => { return {
    }; }
});
